// AutoPlayGallery.jsx - Updated for 3GP detection
import React, { useState, useEffect, useCallback, useRef } from 'react'
import { linkPath, linkPathImageChat, base_url } from '../../../share/helper'
import ReactPlayer from 'react-player'

export default function AutoPlayGallery({ 
  items = [], 
  initialIndex = 0, 
  isOpen = false, 
  onClose,
  autoPlaySpeed = 3000,
  showControls = true,
  showThumbnails = true,
  showCounter = true,
  loop = true
}) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex)
  const [isPlaying, setIsPlaying] = useState(true)
  const [isLoading, setIsLoading] = useState(true)
  const [touchStartX, setTouchStartX] = useState(0)
  const [touchEndX, setTouchEndX] = useState(0)
  const [videoDuration, setVideoDuration] = useState(0)
  const [videoProgress, setVideoProgress] = useState(0)
  const timerRef = useRef(null)
  const containerRef = useRef(null)
  const playerRef = useRef(null)

  // 🔥 Get filename from item
  const getFileName = (item) => {
    if (!item) return ''
    const name = item.originalName || item.filename || item.name || ''
    if (name) return name
    if (item.url) {
      const urlParts = item.url.split('/')
      return urlParts[urlParts.length - 1] || ''
    }
    return ''
  }

  // 🔥 Get base URL for images
  const getImageUrl = (item) => {
    if (!item) return ''
    const url = item.url || item.src || item.filename || ''
    if (!url) return ''
    
    if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:') || url.startsWith('blob:')) {
      return url
    }
    
    if (item.is_group || url.startsWith('/photo/group')) {
      return linkPathImageChat + url.replace(/^\//, '')
    }
    
    if (url.startsWith('/photo/chat')) {
      const base = linkPathImageChat || (base_url ? base_url.replace(/\/api\/?$/, '') : '')
      return base + url
    }
    
    return linkPath + url.replace(/^\//, '')
  }

  // 🔥 Check if file is .3gp
  const is3gpFile = (item) => {
    if (!item) return false
    const fileName = getFileName(item).toLowerCase()
    const mime = (item.mime || item.type || '').toLowerCase()
    const url = (item.url || '').toLowerCase()
    
    return fileName.endsWith('.3gp') || 
           mime.includes('3gp') || 
           mime.includes('3gpp') ||
           url.endsWith('.3gp')
  }

  // 🔥 Get file type - FIXED for .3gp
  const getFileType = (item) => {
    if (!item) return 'image'
    
    const mime = (item.mime || item.type || '').toLowerCase()
    const fileName = getFileName(item).toLowerCase()
    const url = (item.url || '').toLowerCase()
    const fullName = fileName || url.split('/').pop() || ''
    
    // 🔥 Check if it's a 3gp file
    const is3gp = fullName.endsWith('.3gp') || mime.includes('3gp') || mime.includes('3gpp')
    
    // Video extensions including .3gp
    const videoExts = ['.mp4', '.webm', '.mov', '.avi', '.mkv', '.m4v', '.wmv', '.flv', '.f4v', '.ogv', '.3gp']
    const isVideoExt = videoExts.some(ext => fullName.endsWith(ext))
    
    // 🔥 If it's .3gp or has video mime, treat as video
    if (is3gp || mime.includes('video') || isVideoExt) {
      return 'video'
    }
    
    // Audio extensions
    const audioExts = ['.mp3', '.wav', '.flac', '.aac', '.ogg', '.wma', '.m4a', '.m4b', '.m4p', '.m4r', '.amr', '.awb']
    if (mime.includes('audio') || audioExts.some(ext => fullName.endsWith(ext))) {
      return 'audio'
    }
    
    // Image extensions
    const imageExts = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg', '.ico', '.tiff', '.tif']
    if (mime.includes('image') || imageExts.some(ext => fullName.endsWith(ext))) {
      return 'image'
    }
    
    return 'file'
  }

  const currentItem = items[currentIndex] || null
  const currentUrl = currentItem ? getImageUrl(currentItem) : ''
  const fileType = getFileType(currentItem)
  const is3gp = is3gpFile(currentItem)

  // 🔥 Navigation functions
  const goToNext = useCallback(() => {
    if (items.length === 0) return
    const nextIndex = currentIndex + 1 >= items.length ? (loop ? 0 : currentIndex) : currentIndex + 1
    setCurrentIndex(nextIndex)
    setIsLoading(true)
    setVideoProgress(0)
  }, [currentIndex, items.length, loop])

  const goToPrevious = useCallback(() => {
    if (items.length === 0) return
    const prevIndex = currentIndex - 1 < 0 ? (loop ? items.length - 1 : 0) : currentIndex - 1
    setCurrentIndex(prevIndex)
    setIsLoading(true)
    setVideoProgress(0)
  }, [currentIndex, items.length, loop])

  const goToSlide = (index) => {
    if (index >= 0 && index < items.length) {
      setCurrentIndex(index)
      setIsLoading(true)
      setVideoProgress(0)
    }
  }

  const togglePlay = () => {
    setIsPlaying(!isPlaying)
  }

  // 🔥 Auto-play timer - only for images
  useEffect(() => {
    if (!isOpen) {
      if (timerRef.current) clearInterval(timerRef.current)
      return
    }

    if (isPlaying && fileType === 'image') {
      timerRef.current = setInterval(() => {
        goToNext()
      }, autoPlaySpeed)
    } else {
      if (timerRef.current) clearInterval(timerRef.current)
    }

    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
    }
  }, [isPlaying, goToNext, autoPlaySpeed, isOpen, fileType])

  // 🔥 Keyboard navigation
  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowRight') {
        e.preventDefault()
        goToNext()
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault()
        goToPrevious()
      }
      if (e.key === ' ') {
        e.preventDefault()
        togglePlay()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose, goToNext, goToPrevious])

  // 🔥 Touch events for swipe
  const handleTouchStart = (e) => {
    setTouchStartX(e.touches[0].clientX)
  }

  const handleTouchMove = (e) => {
    setTouchEndX(e.touches[0].clientX)
  }

  const handleTouchEnd = () => {
    const diff = touchStartX - touchEndX
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        goToNext()
      } else {
        goToPrevious()
      }
    }
    setTouchStartX(0)
    setTouchEndX(0)
  }

  const handleImageLoad = () => {
    setIsLoading(false)
  }

  // 🔥 Get file icon
  const getFileIcon = (item) => {
    const name = getFileName(item).toLowerCase()
    if (name.endsWith('.pdf')) return '📄'
    if (name.endsWith('.doc') || name.endsWith('.docx')) return '📝'
    if (name.endsWith('.xls') || name.endsWith('.xlsx')) return '📊'
    if (name.endsWith('.ppt') || name.endsWith('.pptx')) return '📑'
    if (name.endsWith('.zip') || name.endsWith('.rar') || name.endsWith('.7z')) return '📦'
    if (name.endsWith('.mp3') || name.endsWith('.wav') || name.endsWith('.flac') || 
        name.endsWith('.aac') || name.endsWith('.ogg') || name.endsWith('.wma') || 
        name.endsWith('.m4a') || name.endsWith('.amr')) {
      return '🎵'
    }
    if (name.endsWith('.mp4') || name.endsWith('.webm') || name.endsWith('.mov') || name.endsWith('.3gp')) {
      return '🎬'
    }
    return '📎'
  }

  // 🔥 Video progress handler for ReactPlayer (non-3gp videos)
  const handleVideoProgress = (state) => {
    if (state && state.played) {
      setVideoProgress(state.played * 100)
    }
  }

  const handleVideoDuration = (duration) => {
    setVideoDuration(duration)
  }

  const handleVideoEnded = () => {
    if (loop) {
      setVideoProgress(0)
      setIsPlaying(true)
    } else {
      goToNext()
    }
  }

  // 🔥 Render content based on file type
  const renderContent = () => {
    if (!currentItem) return null

    // 🔥 For .3gp files, show a message with download option
    if (is3gp) {
      return (
        <div className="autoplay-3gp-container">
          <div className="autoplay-3gp-icon">🎬</div>
          <div className="autoplay-3gp-title">3GP Video</div>
          <div className="autoplay-3gp-name">{getFileName(currentItem)}</div>
          <div className="autoplay-3gp-message">
            ⚠️ 3GP files cannot be played directly in web browsers.
          </div>
          <div className="autoplay-3gp-message">
            ✅ This format is supported in the mobile app.
          </div>
          <a 
            href={currentUrl} 
            download={getFileName(currentItem)}
            className="autoplay-3gp-download"
            target="_blank"
            rel="noreferrer"
          >
            ⬇ Download to play locally
          </a>
          <video
            src={currentUrl}
            controls
            playsInline
            className="autoplay-3gp-video"
            style={{ 
              width: '100%', 
              maxWidth: '500px', 
              maxHeight: '50vh',
              borderRadius: '8px',
              background: '#000',
              marginTop: '16px'
            }}
          />
        </div>
      )
    }

    switch (fileType) {
      case 'video':
        return (
          <div className="autoplay-video-wrapper">
            <ReactPlayer
              ref={playerRef}
              url={currentUrl}
              className="autoplay-react-player"
              width="100%"
              height="100%"
              playing={isPlaying}
              controls={true}
              loop={loop}
              playsinline
              onReady={() => setIsLoading(false)}
              onProgress={handleVideoProgress}
              onDuration={handleVideoDuration}
              onEnded={handleVideoEnded}
              onError={() => {
                setIsLoading(false)
                console.error('Video playback error')
              }}
              config={{
                file: {
                  attributes: {
                    playsInline: true,
                    controlsList: 'nodownload',
                  }
                }
              }}
            />
            {isLoading && (
              <div className="autoplay-loading">
                <div className="autoplay-spinner"></div>
              </div>
            )}
          </div>
        )

      case 'audio':
        return (
          <div className="autoplay-audio-container">
            <div className="autoplay-audio-icon">🎵</div>
            <div className="autoplay-audio-name">
              {getFileName(currentItem) || 'Audio file'}
            </div>
            <ReactPlayer
              url={currentUrl}
              width="100%"
              height="60px"
              playing={isPlaying}
              controls={true}
              onReady={() => setIsLoading(false)}
              onError={() => {
                setIsLoading(false)
                console.error('Audio playback error')
              }}
            />
          </div>
        )

      case 'image':
        return (
          <img
            src={currentUrl}
            alt={getFileName(currentItem) || 'Image'}
            className="autoplay-media autoplay-image"
            onLoad={handleImageLoad}
            onError={() => setIsLoading(false)}
          />
        )

      default:
        return (
          <div className="autoplay-file-container">
            <div className="autoplay-file-icon">{getFileIcon(currentItem)}</div>
            <div className="autoplay-file-name">
              {getFileName(currentItem) || 'File'}
            </div>
            <a 
              href={currentUrl} 
              download={getFileName(currentItem)}
              className="autoplay-file-download"
              target="_blank"
              rel="noreferrer"
            >
              ⬇ Download
            </a>
          </div>
        )
    }
  }

  if (!isOpen || items.length === 0) return null

  return (
    <div 
      className="autoplay-modal"
      ref={containerRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onClick={(e) => {
        if (e.target === containerRef.current) onClose()
      }}
    >
      <div className="autoplay-content">
        {/* Close button */}
        <button 
          className="autoplay-close-btn"
          onClick={onClose}
          aria-label="Close gallery"
        >
          ✕
        </button>

        {/* Play/Pause button - only for image slideshows */}
        {fileType === 'image' && (
          <button 
            className="autoplay-play-btn"
            onClick={togglePlay}
            aria-label={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? '⏸' : '▶'}
          </button>
        )}

        {/* Counter */}
        {showCounter && (
          <div className="autoplay-counter">
            {currentIndex + 1} / {items.length}
            {fileType !== 'image' && (
              <span className="autoplay-file-type-badge">
                {' '}{fileType.toUpperCase()}
              </span>
            )}
          </div>
        )}

        {/* Main content */}
        <div className="autoplay-main">
          {renderContent()}

          {/* Caption */}
          {currentItem?.caption && (
            <div className="autoplay-caption">
              {currentItem.caption}
            </div>
          )}
        </div>

        {/* Navigation arrows */}
        {showControls && items.length > 1 && (
          <>
            <button 
              className="autoplay-nav-btn autoplay-prev"
              onClick={goToPrevious}
              aria-label="Previous"
            >
              ❮
            </button>
            <button 
              className="autoplay-nav-btn autoplay-next"
              onClick={goToNext}
              aria-label="Next"
            >
              ❯
            </button>
          </>
        )}

        {/* Thumbnails */}
        {showThumbnails && items.length > 1 && (
          <div className="autoplay-thumbnails">
            {items.map((item, index) => {
              const itemType = getFileType(item)
              const icon = itemType === 'video' ? '🎬' : itemType === 'audio' ? '🎵' : ''
              const thumbUrl = itemType === 'image' ? getImageUrl(item) : ''
              const displayName = getFileName(item) || `Item ${index + 1}`
              
              return (
                <div
                  key={index}
                  className={`autoplay-thumbnail ${index === currentIndex ? 'active' : ''}`}
                  onClick={() => goToSlide(index)}
                  title={displayName}
                >
                  {thumbUrl && itemType === 'image' ? (
                    <img
                      src={thumbUrl}
                      alt={displayName}
                      onError={(e) => {
                        e.target.style.display = 'none'
                        e.target.parentElement.textContent = icon || '📎'
                      }}
                    />
                  ) : (
                    <span className="autoplay-thumbnail-icon">{icon || '📎'}</span>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>

      <style jsx>{`
        .autoplay-modal {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.92);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          padding: 20px;
        }

        .autoplay-content {
          position: relative;
          width: 100%;
          height: 100%;
          max-width: 1200px;
          max-height: 90vh;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
        }

        .autoplay-close-btn {
          position: absolute;
          top: 10px;
          right: 20px;
          background: rgba(255, 255, 255, 0.2);
          border: none;
          color: white;
          font-size: 28px;
          cursor: pointer;
          width: 50px;
          height: 50px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.3s;
          z-index: 10;
        }

        .autoplay-close-btn:hover {
          background: rgba(255, 255, 255, 0.3);
        }

        .autoplay-counter {
          position: absolute;
          top: 20px;
          left: 50%;
          transform: translateX(-50%);
          color: rgba(255, 255, 255, 0.8);
          font-size: 14px;
          font-weight: 500;
          background: rgba(0, 0, 0, 0.5);
          padding: 6px 16px;
          border-radius: 20px;
          z-index: 10;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .autoplay-file-type-badge {
          background: rgba(255, 255, 255, 0.15);
          padding: 2px 8px;
          border-radius: 12px;
          font-size: 11px;
          text-transform: uppercase;
        }

        .autoplay-main {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          width: 100%;
          height: 100%;
          position: relative;
        }

        .autoplay-media {
          max-width: 100%;
          max-height: 70vh;
          object-fit: contain;
          border-radius: 8px;
          box-shadow: 0 4px 30px rgba(0, 0, 0, 0.5);
        }

        .autoplay-video-wrapper {
          width: 100%;
          height: 100%;
          max-height: 75vh;
          position: relative;
          background: #000;
          border-radius: 8px;
          overflow: hidden;
        }

        .autoplay-react-player {
          position: absolute;
          top: 0;
          left: 0;
          width: 100% !important;
          height: 100% !important;
        }

        .autoplay-caption {
          color: rgba(255, 255, 255, 0.9);
          font-size: 16px;
          margin-top: 16px;
          text-align: center;
          padding: 0 20px;
          max-width: 80%;
        }

        .autoplay-loading {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 5;
        }

        .autoplay-spinner {
          width: 50px;
          height: 50px;
          border: 4px solid rgba(255, 255, 255, 0.1);
          border-top-color: #fff;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .autoplay-nav-btn {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          background: rgba(255, 255, 255, 0.15);
          border: none;
          color: white;
          font-size: 36px;
          cursor: pointer;
          width: 60px;
          height: 80px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.3s;
          z-index: 10;
          border-radius: 8px;
        }

        .autoplay-nav-btn:hover {
          background: rgba(255, 255, 255, 0.25);
        }

        .autoplay-prev {
          left: 10px;
        }

        .autoplay-next {
          right: 10px;
        }

        .autoplay-thumbnails {
          display: flex;
          gap: 8px;
          overflow-x: auto;
          padding: 12px 0;
          margin-top: 16px;
          width: 100%;
          max-width: 80%;
          justify-content: center;
          flex-wrap: wrap;
        }

        .autoplay-thumbnail {
          width: 60px;
          height: 60px;
          border-radius: 8px;
          overflow: hidden;
          cursor: pointer;
          opacity: 0.5;
          transition: all 0.3s;
          border: 2px solid transparent;
          flex-shrink: 0;
          background: rgba(255, 255, 255, 0.1);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .autoplay-thumbnail:hover {
          opacity: 0.8;
        }

        .autoplay-thumbnail.active {
          opacity: 1;
          border-color: #fff;
          transform: scale(1.05);
        }

        .autoplay-thumbnail img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .autoplay-thumbnail-icon {
          font-size: 24px;
        }

        .autoplay-audio-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 16px;
          padding: 40px;
          background: rgba(255, 255, 255, 0.05);
          border-radius: 16px;
          min-width: 300px;
          width: 100%;
          max-width: 600px;
        }

        .autoplay-audio-icon {
          font-size: 64px;
        }

        .autoplay-audio-name {
          color: rgba(255, 255, 255, 0.8);
          font-size: 16px;
        }

        .autoplay-file-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 16px;
          padding: 40px;
          background: rgba(255, 255, 255, 0.05);
          border-radius: 16px;
          min-width: 300px;
        }

        .autoplay-file-icon {
          font-size: 72px;
        }

        .autoplay-file-name {
          color: rgba(255, 255, 255, 0.8);
          font-size: 16px;
          text-align: center;
          max-width: 400px;
          word-break: break-all;
        }

        .autoplay-file-download {
          background: #1890ff;
          color: white;
          padding: 10px 24px;
          border-radius: 8px;
          text-decoration: none;
          font-weight: 500;
          transition: background 0.3s;
        }

        .autoplay-file-download:hover {
          background: #40a9ff;
          color: white;
        }

        /* 🔥 3GP specific styles */
        .autoplay-3gp-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 30px;
          background: rgba(255, 255, 255, 0.05);
          border-radius: 16px;
          min-width: 300px;
          max-width: 600px;
          width: 100%;
        }

        .autoplay-3gp-icon {
          font-size: 72px;
          margin-bottom: 12px;
        }

        .autoplay-3gp-title {
          font-size: 24px;
          font-weight: bold;
          color: rgba(255, 255, 255, 0.9);
          margin-bottom: 4px;
        }

        .autoplay-3gp-name {
          font-size: 16px;
          color: rgba(255, 255, 255, 0.6);
          margin-bottom: 16px;
          text-align: center;
          word-break: break-all;
        }

        .autoplay-3gp-message {
          font-size: 14px;
          color: rgba(255, 255, 255, 0.7);
          text-align: center;
          margin-bottom: 4px;
        }

        .autoplay-3gp-download {
          background: #1890ff;
          color: white;
          padding: 12px 32px;
          border-radius: 8px;
          text-decoration: none;
          font-weight: 500;
          transition: background 0.3s;
          margin-top: 12px;
        }

        .autoplay-3gp-download:hover {
          background: #40a9ff;
          color: white;
        }

        .autoplay-3gp-video {
          max-width: 100%;
          border-radius: 8px;
          background: #000;
        }

        /* Mobile responsive */
        @media (max-width: 720px) {
          .autoplay-nav-btn {
            width: 40px;
            height: 60px;
            font-size: 24px;
          }

          .autoplay-prev {
            left: 4px;
          }

          .autoplay-next {
            right: 4px;
          }

          .autoplay-close-btn {
            top: 8px;
            right: 12px;
            width: 40px;
            height: 40px;
            font-size: 22px;
          }

          .autoplay-thumbnails {
            max-width: 100%;
            padding: 8px 4px;
          }

          .autoplay-thumbnail {
            width: 40px;
            height: 40px;
          }

          .autoplay-media {
            max-height: 50vh;
          }

          .autoplay-video-wrapper {
            max-height: 55vh;
          }

          .autoplay-counter {
            top: 12px;
            font-size: 12px;
            padding: 4px 12px;
          }

          .autoplay-audio-container,
          .autoplay-file-container,
          .autoplay-3gp-container {
            min-width: auto;
            width: 90%;
            padding: 20px;
          }

          .autoplay-file-icon,
          .autoplay-3gp-icon {
            font-size: 48px;
          }

          .autoplay-3gp-title {
            font-size: 20px;
          }
        }
      `}</style>
    </div>
  )
}